import { Button, FileField, FileInput, TextInput, required, useNotify, useSimpleFormIteratorItem } from "react-admin";
import { Box, Typography } from "@mui/material";
import { useWatch } from "react-hook-form";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { getFile, previewFileFromUrl } from "server/components/DownloadFile";
import dataProvider from "server/dataProvider";
import { useParams } from "react-router";
import ConfirmModal from "server/components/Modals/ConfirmModal";
import { documentRequests } from "api/documentsApi";
import { employeeRequests } from "api/employeeApi";
import { settings } from "app";

/**
 * Reusable leave-document edit row. Works both at the top level of a form (single-file
 * flow — Sick leaves) and inside a `<SimpleFormIterator>` (multiple-files flow — Vacations).
 *
 * The document-number field is required only when a file is attached to that document.`
 *
 * @param {string} [watchPrefix] - path prefix for the raw `useWatch` (e.g. `documents.0`).
 *   Omit for the top-level/single flow.
 * @param {number} [index] - iterator index; drives the "№N" suffix when present.
 * @param {string} [numberSource="description"] - `source` for the document-number input
 *   for example (`description` for Vacations, `documentNumber` for Sick leaves). TODO: This has to be consistent from BE side
 * @param {Function} [onDocumentDelete] - called after a successful delete (e.g. refetch).
 * @param {{id?: any, name?: string}} [requiredDocType] - when this row maps to a document type
 *   required by the selected reason: drives the file-input label and makes the upload required
 *   (unless an existing/attached file already satisfies it).
 */
export const DocumentEditRow = ({
  watchPrefix,
  index,
  numberSource = "description",
  onDocumentDelete,
  requiredDocType,
}) => {
  const notify = useNotify();
  const documentId = useWatch({ name: watchPrefix ? `${watchPrefix}.documentId` : "documentId" });
  const documentValue = useWatch({ name: watchPrefix ? `${watchPrefix}.document` : "document" });
  const { id: leaveId } = useParams();
  const suffix = index != null ? `№${index + 1}` : "";
  const hasDocument = !!documentValue || !!documentId;
  const isFileRequired = !!requiredDocType && !documentId && !documentValue;
  const getPrivateDocumentByIdURL = `${settings.gatewayApi}/${documentRequests.getDocumentById(documentId, "private")}`;

  const handlePreview = () => previewFileFromUrl(getPrivateDocumentByIdURL, `document-${documentId}`, notify);

  const handleDownload = () => getFile(documentId, notify, "private", true);

  const onDeleteClick = async () => {
    await dataProvider.customRequest(
      employeeRequests.deleteLeaveDocument(leaveId, documentId),
      null,
      "DELETE",
      null,
      true,
    );

    onDocumentDelete?.();
    notify(`Успешно изтрихте документ ${suffix}`.trim(), { type: "success" });
  };

  return (
    <Box>
      <FileInput
        accept={{
          "image/*": [".jpg", ".jpeg", ".png"],
          "application/pdf": [".pdf"],
          "application/msword": [".doc"],
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
        }}
        placeholder={
          <Box sx={{ display: "flex", justifyContent: "center", gap: 2, py: 0.5 }}>
            <UploadFileIcon color="primary" />
            <Typography>Прикачи снимка на документ</Typography>
          </Box>
        }
        label={requiredDocType?.name ? `Снимка на ${requiredDocType.name}` : `Снимка на документ ${suffix}`.trim()}
        source="document"
        validate={isFileRequired ? required() : undefined}
      >
        <FileField
          source="src"
          title="title"
        />
      </FileInput>
      <TextInput
        label="Тип на документа"
        source={numberSource}
        validate={hasDocument ? required() : undefined}
      />
      {documentId && (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mb: 2 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handlePreview}
          >
            Преглед
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={handleDownload}
          >
            Изтегляне
          </Button>
          <ConfirmModal
            onConfirm={onDeleteClick}
            titleContent={`Изтриване на документ ${suffix}`.trim()}
            message={`Сигурни ли сте, че искате да изтриете документ ${suffix} ?`}
            triggerButton={
              <Button
                label="Изтриване"
                variant="contained"
                size="small"
                color="error"
                sx={{ whiteSpace: "nowrap" }}
              />
            }
          />
        </Box>
      )}
    </Box>
  );
};

export const DocumentEditRowIteratorItem = ({ onDocumentDelete, requiredDocuments = [] }) => {
  const { index } = useSimpleFormIteratorItem();
  const requiredDocType = index < requiredDocuments.length ? requiredDocuments[index] : null;
  return (
    <DocumentEditRow
      watchPrefix={`documents.${index}`}
      index={index}
      numberSource="description"
      onDocumentDelete={onDocumentDelete}
      requiredDocType={requiredDocType}
    />
  );
};

export default DocumentEditRow;
